import React from 'react';

// Plain-English terms and privacy pages. These are a stopgap written without
// counsel — clear about what the platform does and doesn't do — not legal advice.
// Rendered above the auth gate in App.jsx so they're readable while signed out.

const UPDATED = 'August 4, 2026';

function LegalShell({ title, children }) {
  return (
    <div className="min-h-dvh bg-slate-50 dark:bg-slate-900">
      <header className="bg-slate-900 border-b border-slate-800 px-4 md:px-6 h-12 flex items-center">
        <a href="/"><img src="/logo-white.svg" alt="Lane Bids" className="h-6 md:h-7" /></a>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Last updated {UPDATED}</p>
        <div className="mt-6 space-y-6 text-sm leading-relaxed text-slate-700 dark:text-slate-300 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900 dark:[&_h2]:text-slate-100 [&_h2]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <p className="mt-10 text-xs text-slate-400 dark:text-slate-500">
          <a href="/terms" className="hover:underline">Terms of Service</a>
          {' · '}
          <a href="/privacy" className="hover:underline">Privacy Policy</a>
        </p>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <section>
        <h2>What Lane Bids is</h2>
        <p>
          Lane Bids is a private, invitation-based platform for licensed vehicle dealers to offer
          wholesale vehicles to other dealers they already do business with, collect bids, and
          track the resulting transactions. It is a venue and record-keeping tool for
          dealer-to-dealer trades — nothing more. There is no public marketplace and no directory of listings: a buying dealership sees only the deals a selling dealership has sent to its lot, and a seller can only send to lots it has connected using that lot's own code.
        </p>
      </section>
      <section>
        <h2>What Lane Bids is not</h2>
        <ul>
          <li>
            <strong>We are not a party to any transaction.</strong> Every deal, bid, sale, and
            purchase is made directly between the selling dealership and the buying dealership.
          </li>
          <li>
            <strong>We do not handle money.</strong> The platform does not process, hold, escrow,
            or transfer payments. Payment for vehicles happens entirely outside the platform, on
            terms the dealerships agree between themselves. "Payment sent" and "payment confirmed"
            statuses are self-reported by the parties and are not verified by us.
          </li>
          <li>
            <strong>We do not handle titles, transport, or inspection.</strong> Title transfer,
            vehicle delivery or pickup, and inspection are the parties' own responsibility.
          </li>
          <li>
            <strong>We do not verify listings.</strong> Vehicle details, condition reports, photos,
            disclosures, and prices are entered by the selling dealership. We make no representation
            that any of it is accurate or complete.
          </li>
        </ul>
      </section>
      <section>
        <h2>Your responsibilities</h2>
        <ul>
          <li>You must be acting on behalf of a licensed vehicle dealer to use the platform.</li>
          <li>Sellers are responsible for the accuracy of their listings and disclosures.</li>
          <li>Buyers are responsible for their own inspection and due diligence before paying.</li>
          <li>A bid you place or accept here is a real commitment to the other dealership. Disputes
            about a transaction are between the dealerships involved; we may provide records
            (deal, bid, and status history) to either party on request.</li>
          <li>Keep your login credentials to yourself; you are responsible for activity under your account.</li>
        </ul>
      </section>
      <section>
        <h2>Subscriptions and billing</h2>
        <p>
          Seller access requires a paid subscription, priced per dealership location. Subscriptions are
          arranged with the platform administrator and billed by invoice — no card details are
          collected on the platform. Buyer access is free, with no subscription or per-deal fee — buying is gated by seller invitation rather than by payment. Every location on a subscribed account is billed, with no limit on users per location. Each physical dealership location must be set up as its own location; running several rooftops under one location is not permitted.
        </p>
      </section>
      <section>
        <h2>Accounts and termination</h2>
        <p>
          Any licensed dealer may create an account, but access to deals is by invitation: you see nothing until a selling dealership connects your lot. Joining an existing dealership is likewise by invitation from that dealership's admin. We may suspend or remove accounts that misuse the platform,
          misrepresent vehicles, or fail to honor accepted bids. You may stop using the platform
          and request deletion of your account at any time; transaction records may be retained
          for the other party's record-keeping.
        </p>
      </section>
      <section>
        <h2>No warranty; limitation of liability</h2>
        <p>
          The platform is provided as-is, without warranty of any kind. To the maximum extent
          permitted by law, Lane Bids and its operators are not liable for losses arising from a
          transaction between dealerships, from inaccurate listing information, from platform
          downtime, or from data loss. Our total liability for any claim is limited to the
          subscription fees you paid in the twelve months before the claim arose.
        </p>
      </section>
      <section>
        <h2>Changes</h2>
        <p>
          We may update these terms; the "Last updated" date above will change when we do.
          Continued use after a change means you accept the updated terms.
        </p>
      </section>
      <section>
        <h2>Contact</h2>
        <p>
          Questions about these terms: use Settings → Support inside the platform, or contact your
          platform administrator.
        </p>
      </section>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <section>
        <h2>What we collect</h2>
        <ul>
          <li>Account details: your name, email address, and the dealership and lot you belong to.</li>
          <li>Business activity: deals, vehicle listings (including VINs, condition details, and
            photos you upload), bids, offers, and transaction status history.</li>
          <li>Basic technical logs (requests to our servers, including IP addresses) kept for
            security and troubleshooting.</li>
        </ul>
        <p>We do not collect payment card details, bank information, or Social Security numbers — the
          platform never handles payment.</p>
      </section>
      <section>
        <h2>How it's used</h2>
        <p>
          Solely to run the platform: showing deals to the buyer lots a seller chooses, notifying
          you about bids and transaction updates (in-app and by email, per your notification
          preferences in Settings), and keeping transaction records for both parties.
        </p>
        <ul>
          <li>We do not sell or rent your information.</li>
          <li>We do not run ads or use third-party advertising or analytics trackers.</li>
        </ul>
      </section>
      <section>
        <h2>Who sees what</h2>
        <ul>
          <li>Members of your own dealership account see your account's activity.</li>
          <li>When your dealership sends a deal to another lot, or bids on one, that counterparty
            sees the related listing, bid, and contact details — that's the product working.</li>
          <li>The platform administrator can see account, deal, and transaction records for
            support and billing purposes.</li>
        </ul>
      </section>
      <section>
        <h2>Where your data lives</h2>
        <p>
          Data is stored on Google Cloud infrastructure in the United States. Login credentials
          are handled by our authentication service (Keycloak); we store your password only there,
          hashed — platform staff cannot read it. Transactional emails are delivered through
          Resend. Nightly backups are kept for disaster recovery.
        </p>
      </section>
      <section>
        <h2>Retention and deletion</h2>
        <p>
          You can request removal of your user account at any time (Settings → Support, or ask
          your account admin). Records of completed transactions may be retained even after an
          account is removed, because the other dealership in those transactions relies on them
          for its own records.
        </p>
      </section>
      <section>
        <h2>Changes and contact</h2>
        <p>
          If this policy changes, the "Last updated" date above will change with it. Questions or
          data requests: Settings → Support inside the platform, or contact your platform
          administrator.
        </p>
      </section>
    </LegalShell>
  );
}
